import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Mail, MessageSquare, Infinity as InfinityIcon, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { hidePaywallUI } from "@/lib/platform";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const HIDE_PAYWALL = hidePaywallUI();

interface SmsQuotaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canFallbackToEmail: boolean;
  onSendByEmail: () => void;
}

export const SmsQuotaDialog = ({ open, onOpenChange, canFallbackToEmail, onSendByEmail }: SmsQuotaDialogProps) => {
  const navigate = useNavigate();
  const [purchasing, setPurchasing] = useState(false);

  const goToTopUp = async () => {
    setPurchasing(true);
    try {
      const { data, error } = await supabase.functions.invoke("purchase-sms-topup", {
        body: { quantity: 1 },
      });
      if (error) throw new Error(error.message);
      if (!data?.url) throw new Error("No checkout URL returned");
      const win = window.open(data.url, "_blank", "noopener,noreferrer");
      if (!win) {
        try { window.top!.location.href = data.url; } catch { window.location.href = data.url; }
      }
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.message || "Could not start checkout");
    } finally {
      setPurchasing(false);
    }
  };

  const goToDisableSms = () => {
    onOpenChange(false);
    navigate("/account/subscription#sms-usage");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <DialogTitle className="text-center">You're out of SMS credits</DialogTitle>
          <DialogDescription className="text-center">
            You've used all your SMS for this billing cycle. Choose how you'd like to continue.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="rounded-lg border bg-muted/30 p-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <p className="font-medium text-sm">Send by email instead</p>
                <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                  <InfinityIcon className="h-3 w-3" /> Unlimited
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Email sends are free and unlimited — all payment requests, receipts and reminders
                can be sent by email at no cost.
              </p>
            </div>
          </div>

          {!HIDE_PAYWALL && (
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="space-y-1">
                <p className="font-medium text-sm">Top up SMS</p>
                <p className="text-xs text-muted-foreground">
                  Add another 50 SMS for £9.99. Credits roll over and never expire.
                </p>
              </div>
            </div>
          )}

          {!HIDE_PAYWALL && (
            <p className="text-xs text-muted-foreground text-center">
              Prefer to only ever use email? You can{" "}
              <button
                type="button"
                onClick={goToDisableSms}
                className="underline underline-offset-2 hover:text-foreground"
              >
                permanently disable SMS
              </button>{" "}
              from the Subscription page.
            </p>
          )}
        </div>

        <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            {!HIDE_PAYWALL && (
              <Button variant="outline" onClick={goToTopUp} disabled={purchasing}>
                {purchasing ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />}
                {purchasing ? "Opening checkout…" : "Top up SMS"}
              </Button>
            )}
            {canFallbackToEmail && (
              <Button
                onClick={() => {
                  onOpenChange(false);
                  onSendByEmail();
                }}
              >
                <Mail className="h-4 w-4" /> Send by email
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SmsQuotaDialog;
